// =============================================================
// documentIntakeStatus.js — what the document screen SAYS about a document
// still being processed.
//
// Everything after the upload itself happens in the background (OCR → summary
// → filing → SharePoint), so the only thing the user ever sees of it is a line
// of text on the Documents list. That line has to be in their language: what is
// happening to their file right now, or what went wrong, never a stage name or
// a status enum.
//
// Mirror of api/src/documentIntakeStatus.js
// — KEEP THE TWO IN LOCKSTEP (both are unit-tested).
// =============================================================

// Order matters: it is the order the pipeline runs in, and the progress bar
// reads its position from this list.
const STAGES = ['summary', 'sort', 'sharepoint'];

const STAGE_SHORT = {
  summary:    'Reading…',
  sort:       'Filing…',
  sharepoint: 'Saving…',
};

const STAGE_LABELS = {
  summary:    'Reading the document…',
  sort:       'Working out where it goes…',
  sharepoint: 'Saving it to SharePoint…',
};

// tone drives the colour treatment; keep it to these four so both clients agree.
//   busy  — in flight, nothing for the user to do
//   ok    — finished well
//   warn  — finished, but with something the user might want to know
//   error — finished badly; this is the ONLY tone that surfaces a failure, and
//           it surfaces here on the row, never as a popup or a notification.
// `short` is the badge in the narrow Status column; `text` is the full sentence
// shown under the file name. Both say the same thing at different widths.
function intakeStatusLabel(doc = {}) {
  const status = String(doc.AiIntakeStatus || doc.aiIntakeStatus || '').toLowerCase();
  const stage  = String(doc.AiIntakeStage  || doc.aiIntakeStage  || '').toLowerCase();
  const folder = doc.FolderPath || doc.folderPath || null;
  const error  = doc.AiIntakeError || doc.aiIntakeError || null;
  const summaryStatus = String(doc.AiDocSummaryStatus || doc.aiDocSummaryStatus || '').toLowerCase();

  if (status === 'error') {
    return { tone: 'error', short: 'Failed', text: 'Couldn\'t finish processing this file', detail: error || null, busy: false };
  }
  if (status === 'pending') {
    return { tone: 'busy', short: 'Queued', text: 'Queued for processing…', detail: null, busy: true };
  }
  if (status === 'running') {
    return { tone: 'busy', short: STAGE_SHORT[stage] || 'Working…', text: STAGE_LABELS[stage] || 'Processing…', detail: null, busy: true };
  }
  if (status === 'done') {
    // A file we couldn't read still got filed — say so plainly rather than
    // claiming a summary that isn't there.
    if (summaryStatus === 'unsupported') {
      return {
        tone: 'warn', short: 'Filed',
        text: folder ? `Filed in ${folder} — couldn't read this file type` : 'Filed — couldn\'t read this file type',
        detail: null, busy: false,
      };
    }
    if (summaryStatus === 'error') {
      return {
        tone: 'warn', short: 'Filed',
        text: folder ? `Filed in ${folder} — summary unavailable` : 'Filed — summary unavailable',
        detail: error || null, busy: false,
      };
    }
    return { tone: 'ok', short: 'Filed', text: folder ? `Filed in ${folder}` : 'Filed', detail: null, busy: false };
  }
  return null;   // not an intake-tracked document (pre-migration, or an old row)
}

// 0..1 through the pipeline, for a progress bar. A finished run is 1; a run
// that never started is 0.
function intakeProgress(doc = {}) {
  const status = String(doc.AiIntakeStatus || doc.aiIntakeStatus || '').toLowerCase();
  if (status === 'done' || status === 'error') return 1;
  if (status === 'pending') return 0;
  const stage = String(doc.AiIntakeStage || doc.aiIntakeStage || '').toLowerCase();
  const idx = STAGES.indexOf(stage);
  if (idx === -1) return 0;
  return (idx + 0.5) / STAGES.length;
}

// Is this document still moving? Drives the clients' bounded poll.
function intakeIsBusy(doc = {}) {
  const s = String(doc.AiIntakeStatus || doc.aiIntakeStatus || '').toLowerCase();
  return s === 'pending' || s === 'running';
}


// ── The scoreboard: THREE pipelines, counted separately ──────────────────────
//
// One combined set of buckets could not tell the truth about a re-run. With
// "in progress" taking precedence over every other state — which is what made
// the numbers reconcile — re-queueing the book moved thousands of documents
// that HAVE summaries into the working column, and the panel read as though
// 6,000 summaries had evaporated. None had.
//
// So each stage is scored on its OWN state, independent of the queue, and each
// set sums to the total on its own. "In progress" is reported once, separately,
// because it cuts across all three.
//
//   reading    — has Claude read the document?
//   filing     — has Claude chosen its folder?
//   sharepoint — is the file actually in SharePoint?
const PIPELINES = [
  { key: 'reading',    title: 'Read by Claude',    doneKey: 'done',
    order: ['done', 'unreadable', 'failed', 'notStarted'],
    labels: { done: 'Read', unreadable: "Can't be read", failed: 'Failed', notStarted: 'Not read yet' } },
  { key: 'filing',     title: 'Filed by Claude',   doneKey: 'done',
    order: ['done', 'userChose', 'failed', 'notStarted'],
    labels: { done: 'Filed', userChose: 'Folder chosen by hand', failed: 'Failed', notStarted: 'Not filed yet' } },
  { key: 'sharepoint', title: 'In SharePoint',     doneKey: 'done',
    order: ['done', 'pending', 'failed', 'notStarted'],
    labels: { done: 'Uploaded', pending: 'Waiting', failed: 'Failed', notStarted: 'Not uploaded' } },
];

function pipelineSummary(spec, counts = {}, total = 0) {
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const parts = spec.order.map(k => ({ key: k, label: spec.labels[k], value: n(counts[k]) }));
  const bucketed = parts.reduce((sum, p) => sum + p.value, 0);
  const done = n(counts[spec.doneKey]);
  // Outstanding = still owed. A document that CAN'T be read is finished — video
  // and archives have no text and never will — but a FAILED one is not: the
  // failure is usually about the run rather than the file (an exhausted credit
  // balance failed thousands in one go), and the retry button offers exactly
  // these. Counting them as settled put 95% on a bar whose own header said
  // 6,319 of 9,087.
  const outstanding = n(counts.notStarted) + n(counts.failed);
  const settled = Math.max(0, total - outstanding);
  return {
    key: spec.key, title: spec.title, parts, done, outstanding, settled, bucketed,
    // Never round up to 100 while anything is still owed — a full bar beside
    // "36 not uploaded" contradicts itself.
    percent: total ? (outstanding === 0 ? 100 : Math.min(99, Math.round((settled / total) * 100))) : 0,
    complete: total > 0 && outstanding === 0,
    reconciles: total === 0 || bucketed === total,
  };
}

// ── PER-STAGE FLOW: which queue, how fast, how long ─────────────────────────
//
// The panel used to quote ONE queue depth, ONE rate and ONE ETA across three
// stages that run at completely different speeds, so "1,424 documents in the
// queue… about 4 hours left" could not answer the first question anyone asks of
// it: WHICH queue. Those 1,424 were waiting on Claude to read them, on Claude to
// file them, and on Graph to upload them, in unknown proportion — and the single
// ETA was really the reading ETA wearing all three hats.
//
// Each stage now answers for itself, from its own queue and its own completion
// timestamp. The buckets above say where the BOOK stands; these say what is
// moving right now.
const STAGE_FLOW = [
  { key: 'reading',    title: 'Read by Claude',  noun: 'to read' },
  { key: 'filing',     title: 'Filed by Claude', noun: 'to file' },
  { key: 'sharepoint', title: 'In SharePoint',   noun: 'to upload' },
];

// How long work must sit before "nothing is finishing" means anything. Matches
// the sweep's stuck threshold: below it, a queued document has not yet been
// waiting long enough for the safety net to consider it either.
const STALL_GRACE_MS = 15 * 60 * 1000;

function stageFlow(spec, s = {}) {
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  // null is a real answer and must survive: it means "this build cannot measure
  // that", which is different from zero. A pre-165 schema has no placement
  // timestamp, and the reconciler never marks a row running.
  const orNull = (v) => (v === null || v === undefined ? null : n(v));

  const queued   = n(s.queued);
  const running  = orNull(s.running);
  const failed   = n(s.failed);
  const blocked  = orNull(s.blocked);
  const lastHour = orNull(s.lastHour);
  // When the oldest thing still waiting was last touched. Without it "stalled"
  // fires the moment work is queued — see the grace period below.
  const oldestWaiting = s.oldestWaitingUTC ? Date.parse(s.oldestWaitingUTC) : null;
  const waitingForMs = Number.isFinite(oldestWaiting) ? Math.max(0, Date.now() - oldestWaiting) : null;

  // What this stage still owes. `blocked` is deliberately NOT counted: those
  // documents are waiting on an earlier stage, so charging them to this one
  // would blame SharePoint for a reading backlog — which is exactly how "1,370
  // Waiting" read when the two were reported as one number.
  const waiting = queued + n(running);

  // This stage's OWN rate, from its own timestamp. No rate means no estimate:
  // a queue divided by a guess looks like knowledge and isn't.
  const hoursLeft = lastHour !== null && lastHour > 0 && waiting > 0 ? waiting / lastHour : null;

  return {
    key: spec.key, title: spec.title, noun: spec.noun,
    queued, running, failed, blocked, lastHour, waiting, hoursLeft,
    eta: etaLabel(hoursLeft),
    rateLabel: lastHour !== null && lastHour > 0
      ? `${lastHour.toLocaleString()} in the last hour` : null,
    waitingForMs,
    // Idle-but-owed is the shape of a stuck stage, and it is the one thing
    // these numbers can say that the bucket counts cannot — BUT NOT IN THE
    // FIRST MINUTES OF A RUN.
    //
    // `lastHour` counts COMPLETIONS in the last hour, so a stage that has just
    // been handed work has zero of them by definition. Queue 359 documents and
    // look at the panel nineteen seconds later and it said the stage was
    // stalled, which is the most misleading moment it could possibly say it: a
    // person has just pressed a button and is watching to see whether it
    // worked. A dashboard that cries wolf on its own success is worse than one
    // that says nothing.
    //
    // So the work has to have been sitting long enough that something SHOULD
    // have moved. The grace period is STALL_GRACE_MS, which matches the sweep's
    // own stuck threshold — up to that point a queued document has not yet been
    // waiting long enough for even the safety net to look at it.
    stalled: waiting > 0 && lastHour === 0
             && waitingForMs !== null && waitingForMs > STALL_GRACE_MS,
  };
}

// Shared by the per-stage and the overall estimate so they cannot word the same
// thing two different ways.
function etaLabel(hoursLeft) {
  if (hoursLeft === null || !(hoursLeft > 0)) return null;
  const mins = Math.round(hoursLeft * 60);
  if (mins < 1) return 'less than a minute left';
  if (mins < 90) return `about ${mins} minute${mins === 1 ? '' : 's'} left`;
  if (hoursLeft < 36) {
    const h = Math.round(hoursLeft * 10) / 10;
    return `about ${h} hour${h === 1 ? '' : 's'} left`;
  }
  return 'more than a day left';
}

// `counts` comes straight from GET /api/documents/intake/status.
function intakeProgressSummary(counts = {}) {
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const total   = n(counts.total);
  const working = n(counts.working);
  const recent  = n(counts.finishedLast10Min);

  const pipelines = PIPELINES.map(spec => pipelineSummary(spec, counts[spec.key] || {}, total));
  const reading = pipelines[0];
  // `counts.stages` is absent on an older API; every stage then reads as an
  // empty queue rather than throwing, and the panel simply shows no flow line.
  const flows = STAGE_FLOW.map(spec => stageFlow(spec, (counts.stages || {})[spec.key] || {}));

  // Whether the book is done is a question about READING — the filing and
  // SharePoint stages only run for documents a given pass actually touches.
  const done    = total > 0 && working === 0 && reading.outstanding === 0;
  const stalled = !done && working > 0 && recent === 0;

  // How long is left, measured from what actually finished — never from an
  // assumed per-document cost. No rate means no estimate: a remaining count
  // over a guess looks like knowledge and isn't.
  const perHour   = recent * 6;
  const remaining = working + reading.outstanding;
  const hoursLeft = perHour > 0 && remaining > 0 ? remaining / perHour : null;

  const eta = etaLabel(hoursLeft);
  const rateLabel = perHour > 0 ? `~${perHour.toLocaleString()} documents an hour` : null;

  let label;
  if (done)         label = `All ${total.toLocaleString()} documents processed`;
  else if (stalled) label = 'Waiting — nothing has finished in the last 10 minutes';
  else if (working) label = `${working.toLocaleString()} of ${total.toLocaleString()} documents in the queue…`;
  else              label = `${reading.outstanding.toLocaleString()} document${reading.outstanding === 1 ? '' : 's'} not read yet`;

  // WHICH queue — the question the single number in that label cannot answer.
  const queueSplit = flows
    .filter(f => f.waiting > 0)
    .map(f => `${f.waiting.toLocaleString()} ${f.noun}`)
    .join(' · ') || null;

  // Exactly the documents the backfill's own scope selects: no summary and not
  // in flight — which `outstanding` already is (never-read plus failed). Adding
  // failures again double-counted them.
  const retryable = reading.outstanding;

  return { total, working, pipelines, reading, retryable,
           percent: reading.percent, done, stalled, label,
           perHour, remaining, hoursLeft, eta, rateLabel,
           // Per-stage flow, in pipeline order, plus the one-line split that
           // says which queue the header's number is actually in.
           flows, queueSplit,
           reconciles: pipelines.every(p => p.reconciles) };
}

export { STAGES, STAGE_LABELS, STAGE_SHORT, PIPELINES, STAGE_FLOW, STALL_GRACE_MS, stageFlow, etaLabel, intakeStatusLabel, intakeProgress, intakeIsBusy, intakeProgressSummary };
